// ────────────────────────────────────────────────────────────────
//  src/utils/assetVersioning.js     (May-7  — duplicate-name support)
// ────────────────────────────────────────────────────────────────
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  limit,
  getDocs,
} from 'firebase/firestore';

// ────────────────────────────────────────────────────────────────
// ENTERPRISE: debug gate for expensive fallback logging.
// Enable in DevTools if needed:
//   window.__DEBUG_ASSET_VERSIONING__ = true
// ────────────────────────────────────────────────────────────────
const __DBG_VER__ =
  typeof window !== 'undefined' &&
  window.__DEBUG_ASSET_VERSIONING__ === true;

/**
 * Resolves the next version info for an asset (e.g., a screenshot or icon).
 * Finds the latest existing asset of the same type (and formId if applicable),
 * **optionally** narrows by identical file name, marks it as not latest,
 * and returns the next version number.
 *
 * @param {Object}  options
 * @param {string}  options.assetType      - 'screenshot', 'icon', etc.
 * @param {string}  options.organizationId - Org scope
 * @param {string=} options.fileName       - (NEW) If provided, restrict search
 *                                           to assets whose canonicalName ==
 *                                           fileName (case-insensitive). Leave
 *                                           undefined to keep prior behaviour.
 * @param {string=} options.formId         - Optional formId (screenshots, etc.)
 * @param {string=} options.status         - Optional status ('draft', 'published')
 * @param {boolean} [options.ignoreCase=true] - Force lower-case match on name
 *
 * @returns {Promise<{ versionNumber:number, previousAssetId:string|null }>}
 */
export const resolveAssetVersionInfo = async ({
  assetType,
  organizationId,
  fileName   = null,
  canonical  = null,        // optional pre-normalized canonicalName
  formId     = null,
  status     = null,
  ignoreCase = true,
}) => {
  try {
 if (!organizationId) {
   throw new Error(
     '[resolveAssetVersionInfo] Missing required field: organizationId.',
   );
 }


    const nameRaw = canonical || fileName || null;
    const canonicalName =
      nameRaw && ignoreCase ? nameRaw.toLowerCase() : nameRaw;

  //  console.log('[resolveAssetVersionInfo] Starting version resolution with:', {
  //    assetType,
  //    organizationId,
  //    canonicalName,
  //    formId,
  //    status,
  //  });

    const assetRef = collection(db, 'assets');
    /* ───── base constraints ───── */
    const constraints = [
      where('organizationId', '==', organizationId),
      where('assetType',      '==', assetType ?? null),
      where('isLatestVersion','==', true),
    ];

    /* optional filters */
    if (canonicalName) {
      constraints.push(where('canonicalName', '==', canonicalName));
  //    console.log('[resolveAssetVersionInfo] Adding canonicalName filter:', canonicalName);
    }

    /* formIds is an ARRAY field, so use array-contains instead of == */
    if (formId) {
      constraints.push(where('formIds', 'array-contains', formId));
  //    console.log('[resolveAssetVersionInfo] Adding formId filter (array-contains):', formId);
    }

    if (status) {
      constraints.push(where('status', '==', status));
  //    console.log('[resolveAssetVersionInfo] Adding status filter:', status);
    }

    /* ───── query latest matching asset ───── */
    const assetQuery = query(
      assetRef,
      ...constraints,
      limit(1),
    );

    const snapshot = await getDocs(assetQuery);
//    console.log(`[resolveAssetVersionInfo] Query returned ${snapshot.size} result(s).`);

    if (snapshot.empty) {
      console.warn('[resolveAssetVersionInfo] No existing latest version found. Returning version 1.');

      // 🔍 Fallback: Run a less strict debug query to list potential candidates
      // ENTERPRISE: only run when explicitly enabled to avoid hot-path overhead.
      if (__DBG_VER__) {
//      console.log('[resolveAssetVersionInfo] Running fallback debug query to help diagnose.');
       const fallbackQuery = query(
       assetRef,
       where('organizationId', '==', organizationId),
       where('assetType',      '==', assetType ?? null),
       // When formId is present, narrow by array-contains on formIds;
       // otherwise, just list recent assets of this type/org.
       ...(formId ? [where('formIds', 'array-contains', formId)] : []),
       limit(10), // keep bounded for logs
    );


      const fallbackSnap = await getDocs(fallbackQuery);
//      console.log(`[resolveAssetVersionInfo] Fallback query returned ${fallbackSnap.size} result(s):`);

      // ─────────────────────────────────────────────────────────────
      // ENTERPRISE: fallback logging can be very expensive in DevTools.
      // Keep it gated + capped to avoid slowing the submit hot-path.
      // ─────────────────────────────────────────────────────────────
      if (__DBG_VER__) {
        console.log(
          `[resolveAssetVersionInfo] Fallback query returned ${fallbackSnap.size} result(s). Showing up to 10:`,
          { organizationId, assetType, canonicalName, formId, status }
        );

        let shown = 0;
        fallbackSnap.forEach((docSnap) => {
          if (shown >= 10) return;
          shown += 1;

          const d = docSnap.data() || {};
          const safeFormIds = Array.isArray(d.formIds) ? d.formIds.slice(0, 5) : [];

          console.log(
            `- ${docSnap.id} | name: ${String(d.name || '')} | formIds: ${safeFormIds.join(',') || '[]'} | status: ${String(d.status || '')}` +
            ` | isLatest: ${Boolean(d.isLatestVersion)} | version: ${Number.isFinite(d.versionNumber) ? d.versionNumber : ''}`
          );
        });

        if (fallbackSnap.size > 10) {
          console.log(`[resolveAssetVersionInfo] (truncated) ${fallbackSnap.size - 10} more not shown.`);
        }
      }
    }
      return { versionNumber: 1, previousAssetId: null };
    }

    /* ───── compute next version ───── */
    const latestDoc   = snapshot.docs[0];
    const latestData  = latestDoc.data();

    const previousAssetId = latestDoc.id; // doc id is authoritative
    const currentVersion  = latestData.versionNumber || 1;
    const nextVersion     = currentVersion + 1;

//    console.log('[resolveAssetVersionInfo] Found previous latest asset:', {
//      previousAssetId,
//      currentVersion,
//      nextVersion,
//      createdAt: latestData.createdAt?.toDate?.() || latestData.createdAt,
//      isLatestVersion: latestData.isLatestVersion,
//    });

    // ─── Optimization: Only update if still marked latest ───
    // (kept commented-out as in your original file)
    /*
    if (latestData.isLatestVersion !== false) {
      console.log(`[resolveAssetVersionInfo] Marking previous asset (${previousAssetId}) as not latest.`);
      await updateDoc(doc(db, 'assets', previousAssetId), {
        isLatestVersion: false,
        updatedAt      : serverTimestamp(),
      });
    } else {
      console.warn(`[resolveAssetVersionInfo] Previous asset (${previousAssetId}) already marked as not latest. Skipping update.`);
    }
    */

    return {
      versionNumber  : nextVersion,
      previousAssetId,
    };
  } catch (error) {
    console.error('[resolveAssetVersionInfo] Error resolving asset version:', error);
    throw error;
  }
};
